import dynamic from "next/dynamic";
import { TileEditWrapperProps } from "components/Tiles/TileEditWrapper";
import { TileComponentType } from "types/apiTypes";
import { RecordItem } from "types/common";
import TilesContextWrapper from "components/Tiles/TilesContextWrapper";
import { APP_LANGUAGES } from "utils/language";
import BalancerProvider from "../commonComponents/BalancerProvider";
const TileEditWrapper = dynamic(() => import("components/Tiles/TileEditWrapper"));

export type SortedTilesProps = {
  tileComponents?: TileComponentType[];
  hasPromoBar?: boolean;
  isClientRendered?: boolean;
  isEditable?: boolean;
  dealId?: string;
  setBgColor?: (color: string) => void;
  additionalTileComponentProps?: RecordItem;
  componentsMap?: Record<string, React.ElementType>;
  additionalTileEditProps?: Partial<TileEditWrapperProps>;
  isVirtualised?: boolean;
  slug?: string;
  language?: APP_LANGUAGES;
};

const SortedTiles = ({
  tileComponents,
  hasPromoBar,
  isClientRendered,
  isEditable,
  additionalTileComponentProps,
  setBgColor,
  componentsMap,
  additionalTileEditProps,
  dealId,
  slug,
  language = APP_LANGUAGES.ENGLISH
}: SortedTilesProps) => {
  const commonProps = {
    hasPromoBar,
    isClientRendered,
    setBgColor,
    dealId,
    slug,
    language
  };

  if (!isEditable) {
    return (
      <TilesContextWrapper>
        <BalancerProvider>
          {tileComponents?.map((comp) => {
            const { component_id, props_values } = comp;
            const uniqueKey = `component_${comp.id}`;
            if (!component_id?.name) return null;
            const CompElement = componentsMap?.[component_id.name];
            if (!CompElement) return null;
            return (
              <CompElement
                key={uniqueKey}
                {...component_id?.props_default}
                {...props_values}
                {...commonProps}
                {...additionalTileComponentProps?.[component_id.name]}
              />
            );
          })}
        </BalancerProvider>
      </TilesContextWrapper>
    );
  }

  return (
    <TilesContextWrapper>
      <BalancerProvider>
        {tileComponents?.map((comp) => {
          const { props_values, component_id } = comp;
          const uniqueKey = `component_${comp.id}`;
          if (!component_id?.name) return null;
          const CompElement = componentsMap?.[component_id.name];
          if (!CompElement) return null;
          return (
            <TileEditWrapper
              tileId={comp.tile_id || ""}
              tileComponentId={comp.id}
              key={uniqueKey}
              component={component_id}
              propValues={props_values}
              isEditable={isEditable}
              {...additionalTileEditProps}
            >
              <CompElement
                key={uniqueKey}
                {...component_id?.props_default}
                {...props_values}
                {...commonProps}
                {...additionalTileComponentProps?.[component_id.name]}
              />
            </TileEditWrapper>
          );
        })}
      </BalancerProvider>
    </TilesContextWrapper>
  );
};

export default SortedTiles;
